import { Checkbox } from '@mui/material';

import { CurrencyCode } from 'further-types/lp/vehicle';
import { Divider } from 'further-ui/components/Layout';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { type ApiResponses } from 'lib/trpcClient';
import { round } from 'lodash';
import React, { useState } from 'react';

type BaseAllocation =
  ApiResponses['capitalAction']['getCommitmentsWithCapitalContributionBalances'][number];

type Props = {
  calledFundsLabel: string;
  currency: CurrencyCode;
  baseAllocations: Array<BaseAllocation>;
  onChange: (
    capitalBreakdown: Array<{
      commitmentId: string;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => void;
  capitalBreakdown: Array<{
    commitmentId: string;
    fundsCalled?: {
      fromCommitmentLcy: number;
      fromRecallableDistributionLcy: number;
    };
  }>;
};

const CapitalContributionCallAllocations: React.FC<Props> = ({
  currency,
  baseAllocations,
  onChange,
  capitalBreakdown,
  calledFundsLabel,
}) => {
  const [excludedCommitmentIds, setExcludedCommitmentIds] = useState<
    Array<string>
  >(
    capitalBreakdown
      .filter(
        (cb) =>
          round(
            (cb.fundsCalled?.fromCommitmentLcy ?? 0) +
              (cb.fundsCalled?.fromRecallableDistributionLcy ?? 0),
            2,
          ) === 0,
      )
      .map((c) => c.commitmentId),
  );

  const capitalContributionAllocations = baseAllocations.map((allocation) => {
    const isExcluded = excludedCommitmentIds.includes(allocation.commitmentId);
    return {
      ...allocation,
      excludeFromAllocation: isExcluded,
      capitalContributionCalledLcy: isExcluded
        ? 0
        : allocation.fundsCalled.fromCommitmentLcy,
      fundsCalled: {
        fromCommitmentLcy: isExcluded
          ? 0
          : allocation.fundsCalled.fromCommitmentLcy,
        fromRecallableDistributionLcy: 0,
      },
    };
  });

  const columns = [
    {
      label: 'Investor',
      key: 'investorLabel',
      width: 250,
    },
    {
      label: 'Total capital contribution',
      key: 'totalCommitmentLcy',
      width: 180,
    },
    {
      label: 'Uncalled capital contribution',
      key: 'unallocatedCommitmentLcy',
      width: 200,
    },
    {
      label: 'Close date',
      key: 'closeDate',
      width: 140,
    },
    {
      label: calledFundsLabel,
      key: 'capitalContributionCalledLcy',
      width: 200,
    },
    {
      label: 'Exclude',
      key: 'excludeFromAllocation',
      width: 100,
      render: (row: {
        commitmentId: string;
        excludeFromAllocation: boolean;
        unallocatedCommitmentRaw: number;
      }) => (
        <Checkbox
          disabled={row.unallocatedCommitmentRaw === 0}
          checked={
            row.excludeFromAllocation || row.unallocatedCommitmentRaw === 0
          }
          onChange={(e) => {
            const newExcludedIds = e.target.checked
              ? [...excludedCommitmentIds, row.commitmentId]
              : excludedCommitmentIds.filter((id) => id !== row.commitmentId);
            setExcludedCommitmentIds(newExcludedIds);
            onChange(
              baseAllocations.map((a) => {
                const isExcluded = newExcludedIds.includes(a.commitmentId);
                return {
                  commitmentId: a.commitmentId,
                  fundsCalled: {
                    fromCommitmentLcy: isExcluded
                      ? 0
                      : a.fundsCalled.fromCommitmentLcy,
                    fromRecallableDistributionLcy: 0,
                  },
                };
              }),
            );
          }}
        />
      ),
    },
  ];

  const rows = capitalContributionAllocations.map((allocation) => ({
    id: allocation.commitmentId,
    commitmentId: allocation.commitmentId,
    investorLabel: allocation.investorLabel,
    totalCommitmentLcy: numberToCurrencyString(allocation.totalCommitmentLcy, {
      currency,
      negativeInBrackets: true,
    }),
    unallocatedCommitmentLcy: numberToCurrencyString(
      allocation.unallocatedCommitmentLcy,
      { currency, negativeInBrackets: true },
    ),
    capitalContributionCalledLcy: numberToCurrencyString(
      allocation.capitalContributionCalledLcy,
      { currency, negativeInBrackets: true },
    ),
    closeDate: allocation.closeDate.toReadable(),
    excludeFromAllocation: allocation.excludeFromAllocation,
    unallocatedCommitmentRaw: allocation.unallocatedCommitmentLcy,
  }));

  return (
    <>
      <Divider />
      <Table scrollable rows={rows} columns={columns} />
    </>
  );
};

export default CapitalContributionCallAllocations;
